import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import CountdownTimer from "@/components/tools/countdown/CountdownTimer";

// Mock data - in real app this would use generated hooks
interface CountdownWidget {
  id: string;
  title: string;
  description?: string;
  target_date: string;
  widget_slug: string;
  theme: "default" | "urgent" | "celebration" | "dark";
  show_seconds: boolean;
  show_description: boolean;
  is_active: boolean;
  is_public: boolean;
  background_color?: string;
  text_color?: string;
  accent_color?: string;
  meta_title?: string;
  meta_description?: string;
  view_count: number;
}

// Mock data for demonstration
const mockWidgets: Record<string, CountdownWidget> = {
  "product-launch-2024": {
    id: "1",
    title: "Product Launch 2024",
    description: "The biggest product launch of the year",
    target_date: "2024-12-31T23:59:59Z",
    widget_slug: "product-launch-2024",
    theme: "default",
    show_seconds: true,
    show_description: true,
    is_active: true,
    is_public: true,
    view_count: 1250,
  },
  "black-friday-2024": {
    id: "2",
    title: "Black Friday Sale",
    description: "Massive discounts coming soon",
    target_date: "2024-11-29T00:00:00Z",
    widget_slug: "black-friday-2024",
    theme: "urgent",
    show_seconds: true,
    show_description: true,
    is_active: true,
    is_public: true,
    view_count: 892,
  },
  "new-year-2025": {
    id: "3",
    title: "New Year Celebration",
    description: "",
    target_date: "2025-01-01T00:00:00Z",
    widget_slug: "new-year-2025",
    theme: "celebration",
    show_seconds: true,
    show_description: false,
    is_active: false,
    is_public: false,
    view_count: 156,
  },
};

export default function WidgetPage() {
  const { slug } = useParams<{ slug: string }>();
  const [widget, setWidget] = useState<CountdownWidget | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadWidget = async () => {
      try {
        if (!slug) {
          setError("Widget slug is required");
          setLoading(false);
          return;
        }

        // In real app, this would use generated hooks:
        // const { data: widget, error } = useCountdownWidget({ slug });

        // Mock API call delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        const foundWidget = mockWidgets[slug];

        if (!foundWidget) {
          setError("Widget not found");
          setLoading(false);
          return;
        }

        if (!foundWidget.is_public || !foundWidget.is_active) {
          setError("Widget is not available");
          setLoading(false);
          return;
        }

        setWidget(foundWidget);

        // Update view count
        // In real app: await incrementViewCount(foundWidget.id);

        // Update page metadata for SEO
        if (foundWidget.meta_title) {
          document.title = foundWidget.meta_title;
        } else {
          document.title = `${foundWidget.title} - Countdown Widget`;
        }

        if (foundWidget.meta_description) {
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc) {
            metaDesc.setAttribute("content", foundWidget.meta_description);
          } else {
            const meta = document.createElement("meta");
            meta.name = "description";
            meta.content = foundWidget.meta_description;
            document.head.appendChild(meta);
          }
        }

        // Add Open Graph meta tags for social sharing
        const ogTitle = document.querySelector('meta[property="og:title"]');
        if (ogTitle) {
          ogTitle.setAttribute("content", foundWidget.title);
        } else {
          const meta = document.createElement("meta");
          meta.setAttribute("property", "og:title");
          meta.content = foundWidget.title;
          document.head.appendChild(meta);
        }

        const ogDescription = document.querySelector(
          'meta[property="og:description"]',
        );
        const description =
          foundWidget.description || `Countdown to ${foundWidget.title}`;
        if (ogDescription) {
          ogDescription.setAttribute("content", description);
        } else {
          const meta = document.createElement("meta");
          meta.setAttribute("property", "og:description");
          meta.content = description;
          document.head.appendChild(meta);
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading widget:", err);
        setError("Failed to load widget");
        setLoading(false);
      }
    };

    loadWidget();
  }, [slug]);

  // Handle widget completion
  const handleComplete = () => {
    console.log("Countdown completed!");
    // In real app, could trigger analytics event or notification
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading countdown widget...</p>
        </div>
      </div>
    );
  }

  if (error || !widget) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">⏰</div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Widget Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            {error ||
              "The countdown widget you're looking for doesn't exist or is not available."}
          </p>
          <button
            onClick={() => window.history.back()}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg font-medium transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        <CountdownTimer
          targetDate={widget.target_date}
          title={widget.title}
          description={widget.description}
          theme={widget.theme}
          showSeconds={widget.show_seconds}
          showDescription={widget.show_description}
          customColors={{
            background: widget.background_color,
            text: widget.text_color,
            accent: widget.accent_color,
          }}
          isWidget={true}
          onComplete={handleComplete}
        />

        {/* Subtle branding for embedded widgets */}
        <div className="text-center mt-4">
          <a
            href={window.location.origin}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            Powered by AIG
          </a>
        </div>
      </div>
    </div>
  );
}
